<template>
  <dropdown
    ref="dropdownRef"
    class="ifp-filter-creators"
    title="Creators"
    @open="handleDropdownOpen()"
  >
    <base-ifp-filter
      label="Creator"
      clearable
      @click="getCreators()"
      @clear-clicked="handleClearClick()"
    >
      <template #value-display>
        <!-- Creators loaded (e.g. dropdown clicked) -->
        <template v-if="creatorsLoaded">
          <!-- One creator selected -->
          <div v-if="hasLoneCreatorSelection" class="lone-creator-value-display">
            <avatar :person="selectedCreators[0]" :size="18" />
          </div>
          <!-- N creators selected -->
          <avatar-gallery 
            v-else-if="hasSelectedCreators"
            :people="selectedCreators" 
            :max-num-avatars="3" 
            :size="18" 
            stack-tightly
            suppress-avatar-tooltip
            suppress-remaining-people-tooltip
          />
        </template>
        <!-- Creators not yet loaded -->
        <template v-else>
          <!-- One creator selected -->
          <template v-if="hasOneSelectedID">
            <!-- Pull from lone get call -->
            <span v-if="isPrefetchingLoneCreator" class="lone-creator-value-display__pseudo-loader">…</span>
            <div v-else class="lone-creator-value-display">
              <avatar :person="prefetchedLoneCreator" :size="18" />
            </div>
          </template>
          <!-- N creators selected (just show "N creators" to avoid hydrating by eager load of creators) -->
          <template v-else-if="hasSelectedIDs">
            <span>{{ selectedIds.length }} creators</span>
          </template>
        </template>
      </template>
    </base-ifp-filter>
    <template #content>
      <loading-placeholder v-if="!creatorsLoaded" :lines="10" />
      <div v-else>
        <sleek-filter-input
          v-if="showFilterInput"
          ref="sleekFilterInputRef"
          v-model="filterQuery"
          placeholder="Search by name…"
          show-small-text
        />
        <ul v-if="creators.length > 0">
          <li
            v-for="creator in filteredOptions"
            :key="creator.id"
            @click="handleSelection(creator.id)"
          >
            <avatar :size="20" :person="creator" />
            <span :class="{'selected': isSelected(creator.id)}">
              {{ creator.name }}
            </span>
          </li>
        </ul>
        <span v-else>
          No creators found
        </span>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Components */

/* Helpers */

/* Data types */
import { IPersonBase } from '@/api/types/response.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import TasksAPI from '@/api/modules/tasks.api'

export default defineComponent({
  name: 'IfpFilterCreators',

  extends: SearchableDropdownMixin,

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      isPrefetchingLoneCreator: false,
      creatorsLoaded: false,
      creators: [] as IPersonBase[],
      prefetchedLoneCreator: null as IPersonBase,

      /* SearchableDropdownMixin configuration */
      allowMultipleSelections: true,
    }
  },

  computed: {
    totalOptionsCount(): number {
      return this.creators.length
    },
    filteredOptions(): IPersonBase[] {
      if (this.filterQuery.length === 0) {
        return this.creators
      }

      return this.creators.filter(person => {
        return person.name
          .toLowerCase()
          .includes(this.filterQuery.toLowerCase())
      })
    },
    hasLoneCreatorSelection(): boolean {
      return this.selectedCreators.length === 1
    },
    hasSelectedCreators(): boolean {
      // Unvetted from backend, for eager label display...
      return this.selectedCreators.length > 0
    },
    hasSelectedIDs(): boolean {
      // Unvetted from backend, for eager label display...
      return this.selectedIds.length > 0
    },
    hasOneSelectedID(): boolean {
      // Unvetted from backend, for eager label display...
      return this.selectedIds.length === 1
    },
    selectedCreators(): IPersonBase[] {
      if (!this.creatorsLoaded) {
        return []
      }
      return this.creators.filter(c => this.selectedIds.includes(c.id))
    },
  },

  watch: {
    selectedIds: {
      immediate: true,
      handler() {
        // Prefetch the one creator for the value display
        if (!this.creatorsLoaded) {
          if (this.hasOneSelectedID) {
            this.prefetchLoneCreator(this.selectedIds[0])
          }
        }
      },
    },
  },

  methods: {
    async getCreators() {
      if (this.creatorsLoaded) {
        return
      }
      try {
        if (this.projectId) {
          this.creators = await InternalProjectsAPI.get_Creators(this.projectId)
        } else {
          this.creators = await TasksAPI.get_AllCreators()
        }
        this.creatorsLoaded = true
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async prefetchLoneCreator(id: number) {
      try {
        this.isPrefetchingLoneCreator = true
        this.prefetchedLoneCreator = await TasksAPI.get_CreatorContactInfo(id)
        return
      } catch (error) {
        // If we're here, the creator ID doesn't exist. Clear it...
        this.clearSelectedIDs()
      } finally {
        this.isPrefetchingLoneCreator = false
      }
    },
    handleClearClick() {
      this.clearSelectedIDs()
    },
  },
})
</script>

<style lang="scss">
.ifp-filter-creators {
  .lone-creator-value-display {
    display: flex;
    align-items: center;
    width: 100%;

    > span {
      @include single-line-ellipsis;
    }

    &__pseudo-loader {
      color: $color--gray-30;
    }

    @include flex-row-horizontal-spacing(4px);
  }
}
</style>
